package com.mgacreative.hotel.domain.repository

import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import com.mgacreative.hotel.core.tenant.TenantSession
import com.mgacreative.hotel.domain.demo.DemoDataProvider
import com.mgacreative.hotel.domain.model.Room
import com.mgacreative.hotel.domain.model.RoomPosition
import com.mgacreative.hotel.domain.model.FloorFeature
import io.github.jan.supabase.postgrest.postgrest

interface RoomRepository {
    suspend fun getRooms(): List<Room>
    suspend fun addRoom(room: Room): Room?
    suspend fun updateRoom(room: Room): Room?
    suspend fun deleteRoom(roomId: String): Boolean
    suspend fun updateRoomStatus(roomId: String, status: String): Boolean
    suspend fun getRoomPositions(): List<RoomPosition>
    suspend fun saveRoomPosition(position: RoomPosition): Boolean
    suspend fun getFloorFeatures(): List<FloorFeature>
    suspend fun saveFloorFeature(feature: FloorFeature): Boolean
    suspend fun deleteFloorFeature(id: String): Boolean
}

class RoomRepositoryImpl(
    private val networkClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession? = null
) : RoomRepository {
    
    override suspend fun getRooms(): List<Room> {
        if (tenantSession?.isDemoMode == true) {
            return DemoDataProvider.roomsState.value
        }
        return try {
            networkClient.client.postgrest["rooms"]
                .select()
                .decodeList<Room>()
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            emptyList()
        }
    }

    override suspend fun addRoom(room: Room): Room? {
        if (tenantSession?.isDemoMode == true) {
            val newRoom = room.copy(id = room.id ?: "demo-r-${(100..999).random()}")
            DemoDataProvider.roomsState.value = DemoDataProvider.roomsState.value + newRoom
            return newRoom
        }
        return try {
            networkClient.client.postgrest["rooms"]
                .insert(room) { select() }
                .decodeSingleOrNull<Room>()
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            null
        }
    }

    override suspend fun updateRoom(room: Room): Room? {
        if (tenantSession?.isDemoMode == true) {
            DemoDataProvider.roomsState.value = DemoDataProvider.roomsState.value.map {
                if (it.id == room.id) room else it
            }
            return room
        }
        return try {
            networkClient.client.postgrest["rooms"]
                .update(room) {
                    filter {
                        eq("id", room.id ?: "")
                    }
                    select()
                }
                .decodeSingleOrNull<Room>()
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            null
        }
    }

    override suspend fun deleteRoom(roomId: String): Boolean {
        if (tenantSession?.isDemoMode == true) {
            DemoDataProvider.roomsState.value = DemoDataProvider.roomsState.value.filterNot { it.id == roomId }
            return true
        }
        return try {
            networkClient.client.postgrest["rooms"]
                .delete {
                    filter {
                        eq("id", roomId)
                    }
                }
            true
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            false
        }
    }

    override suspend fun updateRoomStatus(roomId: String, status: String): Boolean {
        if (tenantSession?.isDemoMode == true) {
            DemoDataProvider.roomsState.value = DemoDataProvider.roomsState.value.map {
                if (it.id == roomId) it.copy(status = status) else it
            }
            return true
        }
        return try {
            networkClient.client.postgrest["rooms"]
                .update({ set("status", status) }) {
                    filter {
                        eq("id", roomId)
                    }
                }
            true
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            false
        }
    }

    override suspend fun getRoomPositions(): List<RoomPosition> {
        return try {
            networkClient.client.postgrest["room_floor_positions"]
                .select()
                .decodeList<RoomPosition>()
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            emptyList()
        }
    }

    override suspend fun saveRoomPosition(position: RoomPosition): Boolean {
        return try {
            networkClient.client.postgrest["room_floor_positions"]
                .upsert(position)
            true
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            false
        }
    }

    override suspend fun getFloorFeatures(): List<FloorFeature> {
        return try {
            networkClient.client.postgrest["floor_features"]
                .select()
                .decodeList<FloorFeature>()
        } catch (e: Exception) {
            emptyList()
        }
    }

    override suspend fun saveFloorFeature(feature: FloorFeature): Boolean {
        return try {
            networkClient.client.postgrest["floor_features"]
                .upsert(feature)
            true
        } catch (e: Exception) {
            false
        }
    }

    override suspend fun deleteFloorFeature(id: String): Boolean {
        return try {
            networkClient.client.postgrest["floor_features"]
                .delete {
                    filter {
                        eq("id", id)
                    }
                }
            true
        } catch (e: Exception) {
            false
        }
    }
}

